import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualCompanyName,
  selectActualFeatureFlags,
  selectPartner,
} from 'containers/App/selectors'
import {
  selectHasPsimaRole,
  selectPsimaPageStatus,
  selectShowPsimaCompanySelector,
} from 'containers/SpearPhishingAgent/accessSelectors'
import {
  GET_PSIMA_COMPANIES_ASYNC,
  PSIMA_PAGE_STATUS,
} from 'containers/SpearPhishingAgent/constants'
import { selectPsimaCompanyIds } from 'containers/SpearPhishingAgent/selectors'

import CompanySelector from 'components/elements/CompanySelector'
import PageWithSidebar from 'components/elements/PageWithSidebar'
import CenteredLoader from 'components/lib/CenteredLoader'
import { Sent, SimulationCampaign } from 'components/lib/DesignSystem/Icons'
import Sidebar from 'components/lib/DesignSystem/Sidebar'
import PageHeader, { PageTitleH3 } from 'components/lib/PageHeader'
import PageTabs from 'components/pages/navigation/PageTabs'

import ChooseCompanyGate from './components/ChooseCompanyGate'
import Dashboard from './components/Dashboard'
import Launched from './components/Launched'
import NotEnabledState, {
  AccessDeniedState,
  TenantUnavailableState,
} from './components/NotEnabledState'
import { PSIMA_PENDO_FEATURES, trackPsimaPageView } from './psimaPendo'

const Wrapper = styled.div`
  height: calc(100vh - var(--navigation-height));
  font-family: var(--t-typography-font-family-font-family-default);
`

const DashboardContainer = styled.div`
  padding: ${({ $newNav }) => ($newNav ? '25px 0 24px' : '24px')};
  background: var(--color-background);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 30px;
  background: var(--global-filter-background);
  box-shadow: var(--shadow-2);
`

const TabsLayout = styled.div`
  display: flex;
  flex-direction: column;
  /* Page-scroll: title + tabs leave with the page. */
  min-height: 100%;
  height: auto;
  overflow: visible;
  box-sizing: border-box;
  /* The host layout owns the horizontal page inset. */
  padding: 0;
  font-family: var(--t-typography-font-family-font-family-default);
`

const PageTitle = styled(PageTitleH3)`
  margin-bottom: 20px;
  flex-shrink: 0;
`

const TabsContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: visible;
`

const PSIMA_TABS = [
  { value: URL_CONST.psima.self, label: 'Dashboard' },
  { value: URL_CONST.psima.launched, label: 'Launched' },
]

// New nav has no band, so the selector rides the title row instead.
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const getPsimaPageFromPath = (pathname) => {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

  if (normalized === URL_CONST.psima.launched) return 'launched'
  if (normalized === URL_CONST.psima.self) return 'dashboard'
  return null
}

const SpearPhishingAgent = () => {
  const history = useHistory()
  const location = useLocation()
  const isNewNav = !!useSelector(selectActualFeatureFlags)?.NEW_PLATFORM_NAV
  const hasPsimaRole = useSelector(selectHasPsimaRole)
  const pageStatus = useSelector(selectPsimaPageStatus)
  const showCompanySelector = useSelector(selectShowPsimaCompanySelector)
  const tenantName = useSelector(selectActualCompanyName)
  const partner = useSelector(selectPartner)
  const psimaCompanyIds = useSelector(selectPsimaCompanyIds)
  const dispatch = useDispatch()
  const shouldLoadCompanies = hasPsimaRole && !!partner
  const shouldRedirect = pageStatus === PSIMA_PAGE_STATUS.REDIRECT

  const requestedCompanies = useRef(false)

  // Client choices belong to the partner; tenant switches must not refetch them.
  useEffect(() => {
    if (shouldLoadCompanies && !requestedCompanies.current) {
      requestedCompanies.current = true
      dispatch(GET_PSIMA_COMPANIES_ASYNC.initiate())
    }
  }, [shouldLoadCompanies, dispatch])

  // One event per page the user actually lands on. Without the ref, any
  // remount of this subtree (StrictMode in dev, a parent key change) replays
  // the view for a page Pendo has already been told about.
  const trackedPage = useRef(null)

  useEffect(() => {
    if (shouldRedirect) return

    const page = getPsimaPageFromPath(location.pathname)
    if (!page) {
      trackedPage.current = null
      return
    }
    if (trackedPage.current === page) return

    trackedPage.current = page
    trackPsimaPageView(page)
  }, [location.pathname, shouldRedirect])

  if (shouldRedirect) {
    return <Redirect to={URL_CONST.dashboard.home} />
  }

  const sidebarOptions = [
    {
      id: 'Psima_Sidebar_Overview_Option',
      label: 'Dashboard',
      Icon: SimulationCampaign,
      onClick: () => history.push(URL_CONST.psima.self),
      selected: matchURL(URL_CONST.psima.self),
      dataPendoFeature: PSIMA_PENDO_FEATURES.SIDEBAR_DASHBOARD,
    },
    {
      id: 'Psima_Sidebar_Launched_Option',
      label: 'Launched',
      Icon: Sent,
      onClick: () => history.push(URL_CONST.psima.launched),
      selected: matchURL(URL_CONST.psima.launched),
      dataPendoFeature: PSIMA_PENDO_FEATURES.SIDEBAR_LAUNCHED,
    },
  ]

  const renderContent = () => {
    switch (pageStatus) {
      case PSIMA_PAGE_STATUS.LOADING:
        return <CenteredLoader style={{ marginTop: '100px' }} />
      case PSIMA_PAGE_STATUS.CHOOSE_COMPANY:
        return <ChooseCompanyGate />
      case PSIMA_PAGE_STATUS.TENANT_UNAVAILABLE:
        return <TenantUnavailableState />
      case PSIMA_PAGE_STATUS.NOT_ENABLED:
        return <NotEnabledState tenantName={tenantName} />
      case PSIMA_PAGE_STATUS.ACCESS_DENIED:
        return <AccessDeniedState tenantName={tenantName} />
      case PSIMA_PAGE_STATUS.READY:
        return (
          <Switch id="Psima_Switch">
            <Route path={URL_CONST.psima.self} exact component={Dashboard} />
            <Route path={URL_CONST.psima.launched} exact component={Launched} />
            <Redirect exact from={URL_CONST.psima.settings} to={URL_CONST.settings.psima} />
          </Switch>
        )
      default:
        return null
    }
  }

  const companySelector = showCompanySelector && (
    <CompanySelector
      style={{ width: '220px' }}
      optionsFilter={
        psimaCompanyIds.length ? (company) => psimaCompanyIds.includes(company.value) : undefined
      }
    />
  )

  if (isNewNav) {
    return (
      <TabsLayout>
        <TitleRow>
          <PageTitle>Phishing Simulation Agent</PageTitle>
          {companySelector}
        </TitleRow>
        <PageTabs id="psima-tabs" tabs={PSIMA_TABS} />
        <TabsContent>
          <DashboardContainer $newNav>{renderContent()}</DashboardContainer>
        </TabsContent>
      </TabsLayout>
    )
  }

  return (
    <Wrapper>
      <PageWithSidebar Sidebar={<Sidebar options={sidebarOptions} />}>
        <div>
          {partner && (
            <Header id="Psima_PageHeader">
              <PageHeader>Phishing Simulation Agent</PageHeader>
              {/* Keep company selection available when route content is denied. */}
              {companySelector}
            </Header>
          )}
          <DashboardContainer>{renderContent()}</DashboardContainer>
        </div>
      </PageWithSidebar>
    </Wrapper>
  )
}

export default SpearPhishingAgent
